import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table } from '../components/Common';

const SCOPE_TYPES = ['URL', 'IP Address', 'API Endpoint', 'CIDR Range', 'Domain', 'Mobile App'];
const YES_NO = [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }];

// The server's message when it sends one, the status when it doesn't — a
// failed save should never leave the user with nothing to act on.
async function errorText(res) {
  const body = await res.json().catch(() => null);
  return body?.error || `Could not save application (HTTP ${res.status})`;
}

function AppNotesPanel({ appId, apiFetch, user, hasGroup }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const loaded = useRef(false);

  useEffect(() => {
    if (!loaded.current && appId) {
      loaded.current = true;
      apiFetch(`/applications/${appId}/notes`).then(r => r.ok ? r.json() : []).then(setNotes).catch(() => {});
    }
  }, [appId, apiFetch]);

  const addNote = async () => {
    if (!text.trim()) return;
    const res = await apiFetch(`/applications/${appId}/notes`, { method: 'POST', body: JSON.stringify({ content: text }) });
    if (res.ok) { setText(''); const r = await apiFetch(`/applications/${appId}/notes`); if (r.ok) setNotes(await r.json()); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await apiFetch(`/applications/${appId}/notes/${noteId}`, { method: 'DELETE' });
    const r = await apiFetch(`/applications/${appId}/notes`); if (r.ok) setNotes(await r.json());
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Notes</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a note..."
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
          style={{ flex: 1, padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'inherit' }} />
        <button onClick={addNote} disabled={!text.trim()}
          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', opacity: text.trim() ? 1 : 0.5 }}>Add</button>
      </div>
      {notes.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {notes.map(note => (
            <div key={note.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 10px', background: 'var(--bg-card)', borderRadius: 6, fontSize: 13 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: 12 }}>{note.author_name || note.author_username}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 11, marginLeft: 8 }}>{new Date(note.created_at).toLocaleString()}</span>
                <div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>{note.content}</div>
              </div>
              {(note.created_by === user?.id || hasGroup('Super Admins')) && (
                <button onClick={() => deleteNote(note.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, padding: '0 4px', flexShrink: 0 }}>x</button>
              )}
            </div>
          ))}
        </div>
      ) : <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>No notes yet.</p>}
    </div>
  );
}

export default function Applications() {
  const { apiFetch, user, hasGroup, isReadOnlyViewer } = useAuth();
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  // 'all' | 'exclude' (hide out-of-scope) | 'only' (out-of-scope only)
  const [scopeFilter, setScopeFilter] = useState('all');
  // '' = every client. MSP accounts only — a client user's list is already
  // scoped to their own client by the API, so the control would do nothing.
  const [clientFilter, setClientFilter] = useState('');
  const [scopeForm, setScopeForm] = useState({ scopeType: '', value: '', description: '' });
  const [scopeItems, setScopeItems] = useState([]);
  const [detailApp, setDetailApp] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importClientId, setImportClientId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importFields, setImportFields] = useState([]);
  const [showFields, setShowFields] = useState(false);
  const [expandedApps, setExpandedApps] = useState({});
  const [appEngagements, setAppEngagements] = useState({});

  const isMsp = user?.isMsp || user?.groups?.includes('Super Admins');
  const canImport = (hasGroup('Engagement Admins') || !!user?.clientId) && !isReadOnlyViewer;
  // Read-only viewers may look, not touch — the API refuses these anyway.
  const canManageApps = !isReadOnlyViewer;
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [appRes, cliRes] = await Promise.all([apiFetch('/applications'), apiFetch('/clients')]);
    // An error body here used to be handed straight to setApps, and the render
    // then blew up on apps.filter — a failing API took the whole page white.
    const appData = await appRes.json().catch(() => null);
    if (appRes.ok && Array.isArray(appData)) {
      setApps(appData);
      setLoadError('');
    } else {
      setApps([]);
      setLoadError(appData?.error || `Could not load applications (HTTP ${appRes.status})`);
    }
    if (cliRes.ok) setClients(await cliRes.json());
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setFormError('');
    setForm({ pciRequired: 'false', companyOwned: 'false', internetFacing: 'false', companyHosted: 'false', paymentFunction: 'false', ipWhitelistNeeded: 'false', outOfScope: false });
    setScopeItems([]);
    setShowModal(true);
  };

  const openEdit = async (app) => {
    setEditing(app);
    setFormError('');
    setForm({
      clientId: app.client_id, name: app.name, customerTrackingId: app.customer_tracking_id || '',
      pciRequired: app.pci_required ? 'true' : 'false', companyOwned: app.company_owned ? 'true' : 'false',
      internetFacing: app.internet_facing ? 'true' : 'false', companyHosted: app.company_hosted ? 'true' : 'false',
      paymentFunction: app.payment_function ? 'true' : 'false', ipWhitelistNeeded: app.ip_whitelist_needed ? 'true' : 'false',
      comments: app.comments || '', outOfScope: !!app.out_of_scope
    });
    const res = await apiFetch(`/applications/${app.id}`);
    if (res.ok) { const data = await res.json(); setScopeItems(data.scope_items || []); }
    setShowModal(true);
  };

  const save = async () => {
    const body = {
      clientId: user?.clientId || form.clientId, name: form.name, customerTrackingId: form.customerTrackingId || null,
      pciRequired: form.pciRequired === 'true', companyOwned: form.companyOwned === 'true',
      internetFacing: form.internetFacing === 'true', companyHosted: form.companyHosted === 'true',
      paymentFunction: form.paymentFunction === 'true', ipWhitelistNeeded: form.ipWhitelistNeeded === 'true',
      comments: form.comments || null, outOfScope: !!form.outOfScope
    };
    setFormError('');
    let appId;
    // Both branches check res.ok. The edit branch never did, so a rejected
    // rename closed the modal as though it had saved.
    if (editing) {
      const res = await apiFetch(`/applications/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) { setFormError(await errorText(res)); return; }
      appId = editing.id;
    } else {
      const res = await apiFetch('/applications', { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) { setFormError(await errorText(res)); return; }
      const created = await res.json();
      appId = created.id;
    }
    // Save new scope items (for new apps, add pending items)
    if (!editing) {
      for (const s of scopeItems) {
        if (!s.id) {
          await apiFetch(`/applications/${appId}/scope`, { method: 'POST', body: JSON.stringify(s) });
        }
      }
    }
    setShowModal(false); setForm({}); setEditing(null); setScopeItems([]); setFormError(''); load();
  };

  const addScope = async () => {
    if (!scopeForm.scopeType || !scopeForm.value) return;
    if (editing) {
      const res = await apiFetch(`/applications/${editing.id}/scope`, { method: 'POST', body: JSON.stringify(scopeForm) });
      if (res.ok) {
        const created = await res.json();
        setScopeItems(prev => [...prev, created]);
      }
    } else {
      setScopeItems(prev => [...prev, { ...scopeForm, _key: Date.now() }]);
    }
    setScopeForm({ scopeType: '', value: '', description: '' });
  };

  const removeScope = async (item) => {
    if (item.id && editing) {
      await apiFetch(`/applications/${editing.id}/scope/${item.id}`, { method: 'DELETE' });
    }
    setScopeItems(prev => prev.filter(s => s === item ? false : true));
  };

  const deleteApp = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    const res = await apiFetch(`/applications/${id}`, { method: 'DELETE' });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    load();
  };

  const openImport = () => {
    setImportResult(null);
    setImportFile(null);
    setImportClientId(user?.clientId || '');
    setShowFields(false);
    setShowImport(true);
    if (importFields.length === 0) {
      apiFetch('/applications/import/fields').then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setImportFields(d.fields || []); }).catch(() => {});
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await apiFetch('/applications/import/template');
      if (!res.ok) throw new Error('Request failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'application-import-template.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Failed to download template'); }
  };

  const handleImport = async () => {
    if (!importFile || (!user?.clientId && !importClientId)) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('clientId', user?.clientId || importClientId);
      const res = await apiFetch('/applications/import', { method: 'POST', body: formData, isFormData: true });
      const data = await res.json();
      if (!res.ok) setImportResult({ error: data.error || 'Import failed' });
      else { setImportResult(data); load(); }
    } catch (err) { setImportResult({ error: err.message }); }
    setImporting(false);
  };

  const viewDetail = async (app) => {
    const res = await apiFetch(`/applications/${app.id}`);
    if (res.ok) setDetailApp(await res.json());
    setShowDetail(true);
  };

  // Open the engagement list in place. Fetched on first open and kept, so
  // collapsing and reopening a row costs nothing.
  const toggleEngagements = useCallback(async (appId) => {
    const opening = !expandedApps[appId];
    setExpandedApps(prev => ({ ...prev, [appId]: opening }));
    if (!opening || appEngagements[appId]) return;
    setAppEngagements(prev => ({ ...prev, [appId]: { loading: true, rows: [] } }));
    try {
      const res = await apiFetch(`/applications/${appId}/engagements`);
      const rows = res.ok ? await res.json() : [];
      setAppEngagements(prev => ({
        ...prev,
        [appId]: { loading: false, rows: Array.isArray(rows) ? rows : [], error: res.ok ? '' : 'Could not load engagements' },
      }));
    } catch {
      setAppEngagements(prev => ({ ...prev, [appId]: { loading: false, rows: [], error: 'Could not load engagements' } }));
    }
  }, [expandedApps, appEngagements, apiFetch]);

  // Options come from the client list, folding in any client that appears on an
  // application, so the filter can never lack an option for a row on screen.
  const clientOptions = (() => {
    const byId = new Map();
    clients.forEach(c => byId.set(c.id, c.name));
    apps.forEach(a => { if (a.client_id && !byId.has(a.client_id)) byId.set(a.client_id, a.client_name || 'Unknown client'); });
    return [...byId.entries()]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const filtered = apps.filter(a => {
    if (clientFilter && a.client_id !== clientFilter) return false;
    // Out-of-scope filter works both ways: hide them, or show only them.
    if (scopeFilter === 'exclude' && a.out_of_scope) return false;
    if (scopeFilter === 'only' && !a.out_of_scope) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [a.name, a.customer_tracking_id, a.client_name].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  const columns = [
    { key: 'client_name', label: 'Client' },
    { key: 'name', label: 'Application', render: (v, row) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{v}</span>
        {row.out_of_scope && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', background: '#6b7280', color: '#fff', borderRadius: 8 }}>
            OUT OF SCOPE
          </span>
        )}
      </span>
    )},
    { key: 'customer_tracking_id', label: 'Tracking ID', render: v => v || '—' },
    { key: 'pci_required', label: 'PCI', render: v => v ? '✅' : '—' },
    { key: 'internet_facing', label: 'Internet', render: v => v ? '✅' : '—' },
    { key: 'scope_count', label: 'Scope', render: v => v || 0 },
    { key: 'engagement_count', label: 'Engagements', render: (v, row) => v > 0 ? (
      <span style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        title="Show engagements for this application"
        onClick={e => { e.stopPropagation(); toggleEngagements(row.id); }}>
        {v}
        <span style={{ fontSize: 9 }}>{expandedApps[row.id] ? '▾' : '▸'}</span>
      </span>
    ) : 0 },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={e => { e.stopPropagation(); viewDetail(row); }}>View</Button>
        <Button style={{ padding: '4px 10px', fontSize: 12 }} onClick={e => { e.stopPropagation(); navigate(`/engagements?newFor=${row.client_id}&app=${row.id}`); }}>+ Test</Button>
        {canManageApps && (
          <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
        )}
        {canManageApps && (
          <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={e => { e.stopPropagation(); deleteApp(v); }}>Delete</Button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Applications</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          {canImport && <Button variant="secondary" onClick={openImport}>Import CSV</Button>}
          {canManageApps && <Button onClick={openNew}>+ New Application</Button>}
        </div>
      </div>

      {loadError && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>
          {loadError}
        </div>
      )}

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Input placeholder="Search by name or tracking ID..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 0, flex: 1, maxWidth: 400 }} />
        {isMsp && (
          <Select label="Client" style={{ marginBottom: 0, minWidth: 220 }}
            value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            options={[{ value: '', label: `All clients (${apps.length})` }, ...clientOptions]} />
        )}
        <Select label="Out of Scope" style={{ marginBottom: 0, minWidth: 220 }}
          value={scopeFilter} onChange={e => setScopeFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All applications' },
            { value: 'exclude', label: 'Hide out of scope' },
            { value: 'only', label: 'Only out of scope' },
          ]} />
        {(clientFilter || scopeFilter !== 'all' || search) && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', paddingBottom: 10 }}>
            Showing {filtered.length} of {apps.length}
          </div>
        )}
      </div>

      <Table columns={columns} data={filtered}
        expandedIds={expandedApps}
        onToggleExpand={toggleEngagements}
        expandRow={row => {
          if (!(row.engagement_count > 0)) return null;
          const state = appEngagements[row.id];
          if (!state || state.loading) {
            return <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading engagements…</div>;
          }
          if (state.error) {
            return <div style={{ fontSize: 13, color: 'var(--danger)' }}>{state.error}</div>;
          }
          if (state.rows.length === 0) {
            return <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No engagements found.</div>;
          }
          return (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Engagements for {row.name}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {state.rows.map(e => (
                  <div key={e.id}
                    onClick={() => navigate(`/engagements/${e.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderRadius: 6, background: 'var(--bg-card)', border: '1px solid var(--border)',
                      cursor: 'pointer', fontSize: 13,
                    }}>
                    <Badge>{e.status}</Badge>
                    <span style={{ fontWeight: 600, flexShrink: 0 }}>{e.name || e.engagement_type}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{e.engagement_type}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, color: 'var(--text-secondary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      <span>{e.tester_name || 'Unassigned'}</span>
                      <span>{e.finding_count} finding{Number(e.finding_count) === 1 ? '' : 's'}</span>
                      <span>{e.start_date ? new Date(e.start_date).toLocaleDateString() : 'No date'}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        }} />

      {/* Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => { setShowDetail(false); setDetailApp(null); }}
        title={detailApp ? detailApp.name : 'Application Details'} width={700}>
        {detailApp && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14, marginBottom: 16 }}>
              <div><strong>Client:</strong> {detailApp.client_name}</div>
              <div><strong>Tracking ID:</strong> {detailApp.customer_tracking_id || '—'}</div>
              <div><strong>PCI Required:</strong> {detailApp.pci_required ? '✅ Yes' : 'No'}</div>
              <div><strong>Company Owned:</strong> {detailApp.company_owned ? '✅ Yes' : 'No'}</div>
              <div><strong>Internet Facing:</strong> {detailApp.internet_facing ? '✅ Yes' : 'No'}</div>
              <div><strong>Company Hosted:</strong> {detailApp.company_hosted ? '✅ Yes' : 'No'}</div>
              <div><strong>Payment/Checkout:</strong> {detailApp.payment_function ? '✅ Yes' : 'No'}</div>
              <div><strong>IP Whitelist:</strong> {detailApp.ip_whitelist_needed ? '✅ Yes' : 'No'}</div>
              <div><strong>Out of Scope:</strong> {detailApp.out_of_scope ? '✅ Yes' : 'No'}</div>
            </div>
            {detailApp.comments && (
              <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'var(--bg-input)', fontSize: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Comments</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{detailApp.comments}</div>
              </div>
            )}
            {detailApp.scope_items?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Scope Items ({detailApp.scope_items.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {detailApp.scope_items.map(s => (
                    <div key={s.id} style={{ display: 'flex', gap: 8, padding: '6px 10px', background: 'var(--bg-input)', borderRadius: 6, fontSize: 13 }}>
                      <Badge>{s.scope_type}</Badge>
                      <span style={{ fontWeight: 500 }}>{s.value}</span>
                      {s.description && <span style={{ color: 'var(--text-secondary)' }}>— {s.description}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <AppNotesPanel appId={detailApp.id} apiFetch={apiFetch} user={user} hasGroup={hasGroup} />
          </div>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditing(null); setScopeItems([]); setFormError(''); }}
        title={editing ? 'Edit Application' : 'New Application'} width={700} noOverlayClose>
        {formError && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', marginBottom: 16, fontSize: 14 }}>
            {formError}
          </div>
        )}
        {!user?.clientId && (
          <Select label="Client *" value={form.clientId || ''} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}
            options={[{ value: '', label: 'Select Client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]} />
        )}
        <Input label="Application Name *" value={form.name || ''} placeholder="e.g. Code Connect, DigitalOne Studio"
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <Input label="Customer Tracking ID" value={form.customerTrackingId || ''} placeholder="Client's internal reference"
          onChange={e => setForm(f => ({ ...f, customerTrackingId: e.target.value }))} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Select label="PCI Required" options={YES_NO} value={form.pciRequired || 'false'} onChange={e => setForm(f => ({ ...f, pciRequired: e.target.value }))} />
          <Select label="Company Owned" options={YES_NO} value={form.companyOwned || 'false'} onChange={e => setForm(f => ({ ...f, companyOwned: e.target.value }))} />
          <Select label="Internet Facing" options={YES_NO} value={form.internetFacing || 'false'} onChange={e => setForm(f => ({ ...f, internetFacing: e.target.value }))} />
          <Select label="Company Hosted" options={YES_NO} value={form.companyHosted || 'false'} onChange={e => setForm(f => ({ ...f, companyHosted: e.target.value }))} />
          <Select label="Payment / Checkout" options={YES_NO} value={form.paymentFunction || 'false'} onChange={e => setForm(f => ({ ...f, paymentFunction: e.target.value }))} />
          <Select label="IP Whitelist Needed" options={YES_NO} value={form.ipWhitelistNeeded || 'false'} onChange={e => setForm(f => ({ ...f, ipWhitelistNeeded: e.target.value }))} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.outOfScope || false}
              onChange={e => setForm(f => ({ ...f, outOfScope: e.target.checked }))} />
            Out of Scope
          </label>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            Excluded from testing scope. Use the Out of Scope filter on the list to show or hide these.
          </div>
        </div>

        {/* Scope Items */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16, marginTop: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>
            Scope Items {scopeItems.length > 0 && <span style={{ fontWeight: 400 }}>({scopeItems.length})</span>}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
            <Select label="" options={[{ value: '', label: 'Type' }, ...SCOPE_TYPES.map(s => ({ value: s, label: s }))]}
              value={scopeForm.scopeType} onChange={e => setScopeForm(f => ({ ...f, scopeType: e.target.value }))} style={{ marginBottom: 0 }} />
            <Input label="" placeholder="e.g., https://example.com" value={scopeForm.value}
              onChange={e => setScopeForm(f => ({ ...f, value: e.target.value }))} style={{ marginBottom: 0 }} />
            <Input label="" placeholder="Description" value={scopeForm.description}
              onChange={e => setScopeForm(f => ({ ...f, description: e.target.value }))} style={{ marginBottom: 0 }} />
            <Button variant="secondary" style={{ padding: '8px 14px', marginBottom: 0 }}
              onClick={addScope} disabled={!scopeForm.scopeType || !scopeForm.value}>Add</Button>
          </div>
          {scopeItems.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {scopeItems.map((s, i) => (
                <div key={s.id || s._key || i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  background: 'var(--bg-input)', borderRadius: 6, fontSize: 13
                }}>
                  <Badge>{s.scope_type || s.scopeType}</Badge>
                  <span style={{ fontWeight: 500, flex: 1 }}>{s.value}</span>
                  {(s.description) && <span style={{ color: 'var(--text-secondary)' }}>— {s.description}</span>}
                  <button onClick={() => removeScope(s)}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>x</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <TextArea label="Comments" value={form.comments || ''} placeholder="Additional details about the application..."
          onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} />

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => { setShowModal(false); setEditing(null); setScopeItems([]); setFormError(''); }}>Cancel</Button>
          <Button onClick={save} disabled={!form.name || (!user?.clientId && !form.clientId)}>
            {editing ? 'Update Application' : 'Create Application'}
          </Button>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImport} onClose={() => setShowImport(false)} title="Import Applications from CSV" width={640}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={downloadTemplate} style={{ padding: '6px 0', fontSize: 13, textDecoration: 'underline' }}>
            Download CSV Template
          </Button>
          <Button variant="ghost" onClick={() => setShowFields(s => !s)} style={{ padding: '6px 0', fontSize: 13, textDecoration: 'underline' }}>
            {showFields ? 'Hide' : 'Show'} available fields
          </Button>
        </div>

        {showFields && (
          <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-input)', borderRadius: 8, fontSize: 12, maxHeight: 260, overflowY: 'auto' }}>
            {importFields.length === 0 ? (
              <span style={{ color: 'var(--text-secondary)' }}>Loading field list...</span>
            ) : importFields.map(f => (
              <div key={f.name} style={{ display: 'flex', gap: 8, padding: '3px 0', alignItems: 'baseline' }}>
                <code style={{ fontWeight: 600, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{f.name}</code>
                {f.required && <span style={{ color: '#ef4444', fontWeight: 600 }}>required</span>}
                <span style={{ color: 'var(--text-secondary)' }}>{f.description}</span>
              </div>
            ))}
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
              Column order does not matter and unknown columns are ignored. Rows whose application name already exists for
              the selected client are skipped rather than duplicated.
            </div>
          </div>
        )}

        {!user?.clientId && (
          <Select label="Assign to Client *"
            options={[{ value: '', label: 'Select Client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
            value={importClientId} onChange={e => setImportClientId(e.target.value)} />
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>CSV File *</label>
          <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])}
            style={{ fontSize: 14, color: 'var(--text)' }} />
        </div>

        {importResult && (
          <div style={{
            padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: importResult.error ? '#7f1d1d' : '#14532d',
            color: importResult.error ? '#fca5a5' : '#86efac'
          }}>
            {importResult.error ? (
              <div>Error: {importResult.error}</div>
            ) : (
              <div>
                <div>
                  Imported {importResult.imported} application{importResult.imported !== 1 ? 's' : ''}
                  {importResult.skipped > 0 && `, skipped ${importResult.skipped} duplicate${importResult.skipped !== 1 ? 's' : ''}`}.
                </div>
                {importResult.unknownColumns?.length > 0 && (
                  <div style={{ marginTop: 8 }}>Ignored unknown columns: {importResult.unknownColumns.join(', ')}</div>
                )}
                {importResult.errors?.length > 0 && (
                  <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto' }}>
                    <div style={{ fontWeight: 600 }}>Row messages:</div>
                    {importResult.errors.map((e, i) => (
                      <div key={i}>Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowImport(false)}>Close</Button>
          <Button onClick={handleImport} disabled={!importFile || (!user?.clientId && !importClientId) || importing}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
